import { fileTypeFromBlob } from "file-type";
import { inferMimeType } from "@/helpers/fileUploads";
import { withPickedFileReadTurn } from "@uploads";

export const UNREADABLE_FILE_MESSAGE =
	"We couldn't read this file. It may not be fully downloaded to this device yet. Open it in your Photos app or wait for the download to finish, then choose it again.";

export type FileTypeCategory = "image" | "video" | "pdf";

const CATEGORY_PATTERNS: Record<FileTypeCategory, RegExp> = {
	image: /^image\/(png|jpeg|webp|heic|heif|gif)$/,
	video: /^video\/.*$/,
	pdf: /^application\/pdf$/
};

const DEFAULT_CATEGORIES: FileTypeCategory[] = ["image", "video", "pdf"];

/** How long to wait for a file slice read before treating the read as inconclusive. */
const SLICE_READ_TIMEOUT_MS = 200;

function matchesAllowed(mime: string, allow: FileTypeCategory[]): boolean {
	// RFC 2045 §5.1: MIME type/subtype tokens are case-insensitive. The magic-byte path (file-type
	// library) always returns lowercase, but the OS-reported fallback (file.type, verbatim) can be
	// uppercase on some browsers/OSes — normalize here so both paths use the same comparison, rather
	// than silently false-rejecting a legitimate "IMAGE/JPEG" on the fallback path only.
	const normalizedMime = mime.toLowerCase();
	return allow.some((category) => CATEGORY_PATTERNS[category].test(normalizedMime));
}

export interface UploadFileValidation {
	valid: boolean;
	/** The picked file could not be read, rather than having an unsupported format. */
	unreadable?: boolean;
	/**
	 * The MIME type actually detected from the file's magic bytes (e.g. `"image/jpeg"`), independent
	 * of the `allow` allowlist — `null` when the magic-byte read was inconclusive (timed out / threw,
	 * see `allowFromOsMimeFallback`) or the bytes were unrecognized. Lets a caller correct a File's
	 * declared `type` when it disagrees with what the content actually is (LSP-17928 break-it round
	 * 4): downstream re-classification (`getFilesByType`, `inferFileCategory`) trusts the declared
	 * `file.type`/extension, a weaker signal than this one, so a mismatched declared type can still
	 * route an approved file to the wrong upload category even though `valid` is true.
	 */
	detectedMimeType: string | null;
}

/**
 * Decides whether to allow a file when the magic-byte read was inconclusive — it
 * either timed out (slow/cloud-hosted file) or the format parser threw after the
 * source read succeeded. With no honest magic-byte signal we fall back to the OS-reported
 * MIME (`file.type`, or extension inference when `file.type` is empty). We allow the file
 * iff that names an accepted type; otherwise we reject.
 *
 * We intentionally do NOT blanket-allow an empty/unknown inferred MIME: the downstream
 * partitioner (`getFilesByType` / `ensureFileType`) also keys off `inferMimeType` and
 * silently drops any file it can't bucket, so allowing an uncategorizable file here would
 * only convert a visible rejection into a silent drop — and an orphan conversation on the
 * ActionUploadPhoto path (LSP-15721 review). The realistic Android case (empty `file.type`
 * with a real extension like `.mp4`) still resolves via extension inference and is allowed;
 * server-side validation remains the backstop.
 *
 * The `reason` is logged so a future inconclusive-read incident is a one-line triage.
 */
function allowFromOsMimeFallback(file: File, allow: FileTypeCategory[], reason: string): boolean {
	const inferredMime = inferMimeType(file);
	console.warn(`validateUploadFile: ${reason}; deferring to OS-reported MIME`, {
		name: file.name,
		fileType: file.type,
		inferredMime
	});
	return matchesAllowed(inferredMime, allow);
}

/**
 * Validates whether a file is an allowed upload type by checking its actual magic
 * bytes rather than trusting the file extension or OS-reported MIME type. A user
 * can rename `evil.html` to `clip.mov` and the browser will happily report
 * `video/quicktime` — magic bytes are the only honest signal we have.
 *
 * For cloud-hosted files (Google Drive, iCloud, etc.) even a small slice read
 * can block for 60+ seconds while the provider sets up a download session. We
 * race the read against a short timeout so a slow read never wedges the picker.
 *
 * On Android the local slice read frequently exceeds the timeout budget, so the
 * magic-byte check was rejecting genuinely valid videos outright (LSP-15721). The
 * failure is intermittent because a warm cache lets the read win the race on retry.
 *
 * A timeout is inconclusive and keeps the OS-reported MIME fallback. A source read
 * that rejects is confirmed unreadable and must be selected again, regardless of MIME.
 * Format-parser errors on already-read bytes retain the existing fallback.
 *
 * @param allow Categories to accept. Defaults to image/video/pdf.
 */
export async function isValidUploadFile(file: File, allow: FileTypeCategory[] = DEFAULT_CATEGORIES): Promise<boolean> {
	return (await validateUploadFile(file, allow)).valid;
}

/**
 * Same magic-byte validation as {@link isValidUploadFile}, but also surfaces the actually-detected
 * MIME type (see {@link UploadFileValidation.detectedMimeType}) so a caller can correct a File whose
 * declared `type`/extension disagrees with its real content before handing it to a downstream
 * classifier that trusts the declared type.
 */
export async function validateUploadFile(file: File, allow: FileTypeCategory[] = DEFAULT_CATEGORIES): Promise<UploadFileValidation> {
	let buffer: ArrayBuffer | null;
	try {
		const slice = file.slice(0, 65536);
		// LSP-22818: the timeout starts inside the Android read gate's turn, so queueing never times a file out, and the
		// gate stays held until the read itself settles, so a timed-out read still open never overlaps the next one.
		buffer = await withPickedFileReadTurn(() => {
			const reading = slice.arrayBuffer();
			return {
				result: Promise.race([reading, new Promise<null>((resolve) => setTimeout(() => resolve(null), SLICE_READ_TIMEOUT_MS))]),
				settled: reading
			};
		});
	} catch (error) {
		console.warn("[Upload] File unreadable during selection; rejecting before upload creation", {
			fileName: file.name,
			fileSize: file.size,
			fileType: file.type,
			fileLastModified: file.lastModified,
			phase: "pick-validation",
			// DOMException's non-enumerable details otherwise reach Datadog as just "[DOMException]".
			errorName: error instanceof Error || error instanceof DOMException ? error.name : undefined,
			errorMessage: error instanceof Error || error instanceof DOMException ? error.message : undefined,
			errorCode: error instanceof DOMException ? error.code : undefined,
			error
		});
		return { valid: false, detectedMimeType: null, unreadable: true };
	}

	try {
		if (buffer !== null) {
			const type = await fileTypeFromBlob(new Blob([buffer]));
			if (type) {
				return { valid: matchesAllowed(type.mime, allow), detectedMimeType: type.mime };
			}
			// Read succeeded but magic bytes unrecognized — reject the file
			return { valid: false, detectedMimeType: null };
		}
		// shortcut: timeout cannot prove readability; revisit admission if late failures become common.
		return {
			valid: allowFromOsMimeFallback(file, allow, `slice read timed out after ${SLICE_READ_TIMEOUT_MS}ms`),
			detectedMimeType: null
		};
	} catch (err) {
		// The source was readable; a format-parser failure does not make it unreadable.
		console.error("Failed to detect file type. Falling back to OS-reported MIME.", err);
		return { valid: allowFromOsMimeFallback(file, allow, "format parser threw"), detectedMimeType: null };
	}
}
